import { Fragment } from "react";
import { useTheme } from "@mui/material";
import { CarPdfBox, CarPdfBoxProps } from "./PdfBox";
import { CarPdfTypography } from "./PdfTypography";
import {
  DecimalOptions,
  formatNumber,
  formatPercentFactor,
  isDefined,
} from "@app/utils";
import { ExcelDataFormat } from "@app/hooks/useExcelExport";
import { CarStatBoxItem } from "./StatBox";
import { Circle, Svg } from "@react-pdf/renderer";

const formatValue = ({
  format,
  value,
  decimalOptions = {
    decimalPlaces: 2,
    forceShowDecimals: true,
  },
}: {
  format?: ExcelDataFormat;
  value?: number | null;
  decimalOptions?: DecimalOptions;
}) =>
  isDefined(value)
    ? format === ExcelDataFormat.float
      ? formatNumber(value, decimalOptions)
      : formatPercentFactor(value, decimalOptions)
    : "-";

const PdfBoxItem = (props: { item: CarStatBoxItem; isLast: boolean }) => {
  const theme = useTheme();
  return (
    <CarPdfBox style={{ flexDirection: "row", flexGrow: 1 }}>
      <CarPdfBox
        style={{
          flex: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          paddingLeft: 8,
          paddingRight: 8,
        }}
      >
        <CarPdfBox
          style={{ flexDirection: "row", alignItems: "center", gap: 6 }}
        >
          {!!props.item.legendColor && (
            <Svg width={9} height={9} viewBox={"0 0 9 9"}>
              <Circle cx={4.5} cy={4.5} r={4.5} fill={theme.palette.gray6} />
              <Circle cx={4.5} cy={4.5} r={3.5} fill={props.item.legendColor} />
            </Svg>
          )}
          <CarPdfTypography
            variant="v9Semibold"
            style={{
              color: theme.palette.softBlack,
              textAlign: "center",
              marginTop: 2,
              letterSpacing: 0.8,
            }}
          >
            {props.item.pdfLabel ?? props.item.label}
          </CarPdfTypography>
        </CarPdfBox>
        {!props.item.hideValue && (
          <CarPdfTypography
            variant="v14Bold"
            style={{ color: props.item.valueColor }}
          >
            {formatValue(props.item)}
          </CarPdfTypography>
        )}
      </CarPdfBox>
      {!props.isLast && (
        <CarPdfBox
          style={{
            borderLeftWidth: 1,
            borderLeftStyle: "solid",
            borderColor: theme.palette.gray3,
            alignSelf: "stretch",
            marginTop: 6,
            marginBottom: 6,
          }}
        />
      )}
    </CarPdfBox>
  );
};

export interface CarPdfStatBoxProps extends CarPdfBoxProps {
  items: CarStatBoxItem[];
  isWhiteContext?: boolean;
}
export const CarPdfStatBox = ({
  items,
  isWhiteContext,
  style,
  ...props
}: CarPdfStatBoxProps) => {
  const theme = useTheme();
  return (
    <CarPdfBox
      style={{
        display: "flex",
        flexDirection: "row",
        borderWidth: 2,
        borderStyle: "solid",
        borderColor: theme.palette.gray3,
        borderRadius: 5,
        minHeight: 55,
        backgroundColor: isWhiteContext
          ? theme.palette.gray1
          : theme.palette.white,
        ...style,
      }}
      {...props}
    >
      {items.map((i, idx, arr) => (
        <Fragment key={idx}>
          <PdfBoxItem item={i} isLast={idx === arr.length - 1} />
        </Fragment>
      ))}
    </CarPdfBox>
  );
};
